import * as React from 'react';

import * as UI from 'core/ui';
import { TicketSummaryDTO } from 'core/api/Types';
import { Res } from 'core/api';
import { DateUtils } from 'core/utils';
import { Box, Table, VStack, Text, HStack } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import z from 'zod';
import { SvgIcon } from 'core/ui-next';
import * as Images from 'pos/Images';


const stateShcema = z.object({
	showAllTickets: z.boolean()
});

type State = z.infer<typeof stateShcema>;

type Props = {
	filteredTickets: TicketSummaryDTO[],
	showAllTickets: boolean,
	onShowAllTicketsChanged: (showAllTickets: boolean) => void,
	onTicketSelected: (ticket: TicketSummaryDTO) => void
};


export default function PhoneSelectTicketScreen(props: Props) {

	const { filteredTickets, showAllTickets, onShowAllTicketsChanged, onTicketSelected } = props;

	const getTotalTicketsAmount = (): number => {
		return filteredTickets.reduce((sum, ticket) => sum + ticket.NetAmount, 0);
	};

	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, CheckBox } = useControlsFor<State>();

	const initialState: State = {
		showAllTickets: showAllTickets
	};

	return <VStack gap={'6'} paddingY={'4'} paddingX={'2'} width={'full'} height={'full'} minH={0}>
		<Box>
			<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.GET_TICKET_IN_LOCATION_RESUME_LBL()}</Text>
		</Box>
		<VStack gap={'4'} width={'full'} minH={0}>
			<HStack width={'full'} padding={'2'}>
				<Form ref={formRef} initialValue={initialState} schema={stateShcema}>
					<CheckBox
						size={'sm'}
						linkTo={'showAllTickets'}
						label={Res.Web.GET_TICKET_IN_LOCATION_SHOW_ALL_TICKETS_LBL()}
						cursor={'pointer'}
						onWatch={value => onShowAllTicketsChanged(value === true)}
					/>
				</Form>
			</HStack>
			<VStack gap={'0'} width={'full'} borderRadius={'md'} shadow={'sm-up'} bg={{ base: 'white', _dark: '#40444F' }} minH={0}>
				<HStack width={'full'} padding={'3'}>
					<Box width={'full'} display={'flex'} justifyContent={'end'} padding={'2'} borderBottomWidth={'2px'} borderColor={'neutral.100'}>
						<Text fontSize={'sm'} fontWeight={'bold'}>{Res.Web.GET_TICKETS_IN_LOCATION_TOTAL_LBL(UI.Format.asMoney(getTotalTicketsAmount()))}</Text>
					</Box>
				</HStack>
				<Table.ScrollArea w={'full'} minH={0} rounded={'md'} overflowX={'auto'}>
					<Table.Root width={'361px'} striped interactive stickyHeader>
						<Table.Header>
							<Table.Row>
								<Table.ColumnHeader w={'60%'} minW={'60%'} maxW={'60%'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'start'}>{Res.Web.GET_TICKET_IN_LOCATION_LOCATION_CLM()}</Table.ColumnHeader>
								<Table.ColumnHeader w={'40%'} minW={'40%'} maxW={'40%'} fontSize={'sm'} fontWeight={'semibold'} textAlign={'end'}>{Res.Web.GET_TICKET_IN_LOCATION_TOTAL_CLM()}</Table.ColumnHeader>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{filteredTickets.map((ticket, index) => {
								const hasOpenerUser = Boolean(ticket.OpenerUser);
								const hasCustomer = Boolean(ticket.CustomerFiscalName !== '');
								const hasPrepayments = Boolean(ticket.HasPrepayments);

								return (
									<Table.Row key={index} onClick={() => onTicketSelected(ticket)} cursor={'pointer'}>
										<Table.Cell w={'60%'} minW={'60%'} maxW={'60%'} fontSize={'sm'} textAlign={'start'} py={'2'}>
											<VStack alignItems={'start'} gap={'0.5'}>
												<Text fontSize={'sm'} lineClamp={2}>
													{UI.Format.asLocationDescription({
														FriendlyName: ticket.FriendlyName,
														Location: { SaleCenterName: ticket.SaleCenterName, SaleLocationName: ticket.SaleLocationName }
													})}
												</Text>
												{hasOpenerUser &&
													<HStack minH={'5'} gap={'0.5'}>
														<>
															<SvgIcon src={Images.User} width={'3'} minW={'3'} height={'3'} minH={'3'} />
															<Text fontSize={'sm'} lineClamp={2}>{ticket.OpenerUser}</Text>
														</>
													</HStack>
												}
												{hasCustomer &&
													<HStack minH={'5'} gap={'0.5'}>
														<>
															<SvgIcon src={Images.User} width={'3'} minW={'3'} height={'3'} minH={'3'} />
															<Text fontSize={'sm'} lineClamp={2}>{ticket.CustomerFiscalName}</Text>
														</>
													</HStack>
												}
											</VStack>
										</Table.Cell>
										<Table.Cell w={'40%'} minW={'40%'} maxW={'40%'} fontSize={'sm'} textAlign={'end'} py={'2'}>
											<VStack alignItems={'end'} gap={'0.5'}>
												<Text fontSize={'sm'}>{UI.Format.asMoney(ticket.NetAmount)}</Text>
												{hasPrepayments &&
													<Box minH={'5'}>
														<>
															<Text fontSize={'xs'} whiteSpace={'break-spaces'}>
																{Res.Web.GET_TICKET_IN_LOCATION_PAID_LBL(UI.Format.asMoney(ticket.NetAmount - ticket.PendingAmount))}
															</Text>
														</>
													</Box>
												}
												<Text fontSize={'sm'}>
													{ticket.CreationDate ? DateUtils.formatDate(ticket.CreationDate, 'dd/MM/yy HH:mm') : ''}
												</Text>
											</VStack>
										</Table.Cell>
									</Table.Row>
								);
							})}
						</Table.Body>
					</Table.Root>
				</Table.ScrollArea>
			</VStack>
		</VStack>
	</VStack>;
}